<template>
  <div>
    <div class="large-top-spacer" id="cardGrid">
      <div class="filter-header-label mb-3 mt-4">
        ↑ Filter
      </div>

      <div class="filter-main-container p-4 mb-4">
        <!-- Categories Filter Row -->
        <div class="scroll-row-wrapper" :class="{'scroll-row-wrapper--expanded': categoryExpanded}">
          <div class="filter-pill scroll-row-all"
               :class="{'active': (filter === 'all')}"
               v-on:click="changeFilter('all')">
            All Categories
          </div>
          <button v-if="!categoryExpanded" type="button"
                  class="scroll-row-arrow scroll-row-arrow--left"
                  :class="{'is-disabled': !categoryCanScrollLeft, 'is-hidden': !categoryHasOverflow}"
                  aria-label="Scroll categories left"
                  v-on:click="scrollRow('categoryScroller', -1)">
            ‹
          </button>
          <div class="filter-row"
               :class="categoryExpanded ? 'filter-row--wrap' : 'filter-row--scroll'"
               ref="categoryScroller"
               v-on:wheel.prevent="onRowWheel('categoryScroller', $event)"
               v-on:scroll="updateCategoryScrollState">
            <div v-for="(item, index) in cardCategories" :key="'cat-'+index"
                 class="filter-pill" :class="{'active': (filter === item.idCategory)}"
                 v-on:click="changeFilter(item.idCategory)">
              {{ item.categoryName }}
            </div>
          </div>
          <button v-if="!categoryExpanded" type="button"
                  class="scroll-row-arrow scroll-row-arrow--right"
                  :class="{'is-disabled': !categoryCanScrollRight, 'is-hidden': !categoryHasOverflow}"
                  aria-label="Scroll categories right"
                  v-on:click="scrollRow('categoryScroller', 1)">
            ›
          </button>
          <button type="button"
                  class="scroll-row-arrow scroll-row-toggle"
                  :class="{'is-hidden': !categoryHasOverflow && !categoryExpanded}"
                  :aria-label="categoryExpanded ? 'Collapse categories' : 'Show all categories'"
                  :title="categoryExpanded ? 'Collapse' : 'Show all'"
                  v-on:click="toggleCategoryExpand">
            {{ categoryExpanded ? '«' : '»' }}
          </button>
        </div>

        <!-- Authors Filter Row -->
        <div class="scroll-row-wrapper" :class="{'scroll-row-wrapper--expanded': authorExpanded}">
          <div class="filter-pill scroll-row-all"
               :class="{'active': (authorFilter === 'all')}"
               v-on:click="changeAuthorFilter('all')">
            All Authors
          </div>
          <div class="filter-pill scroll-row-all"
               v-if="user.loggedIn"
               :class="{'active': (authorFilter === 'mine')}"
               v-on:click="changeAuthorFilter('mine')">
            My Cards
          </div>
          <button v-if="!authorExpanded" type="button"
                  class="scroll-row-arrow scroll-row-arrow--left"
                  :class="{'is-disabled': !authorCanScrollLeft, 'is-hidden': !authorHasOverflow}"
                  aria-label="Scroll authors left"
                  v-on:click="scrollRow('authorScroller', -1)">
            ‹
          </button>
          <div class="filter-row"
               :class="authorExpanded ? 'filter-row--wrap' : 'filter-row--scroll'"
               ref="authorScroller"
               v-on:wheel.prevent="onRowWheel('authorScroller', $event)"
               v-on:scroll="updateAuthorScrollState">
            <div v-for="(author, index) in authors" :key="'auth-'+index"
                 class="filter-pill" :class="{'active': (authorFilter === author.idCompany)}"
                 v-on:click="changeAuthorFilter(author.idCompany)">
              {{ author.companyName }}
            </div>
          </div>
          <button v-if="!authorExpanded" type="button"
                  class="scroll-row-arrow scroll-row-arrow--right"
                  :class="{'is-disabled': !authorCanScrollRight, 'is-hidden': !authorHasOverflow}"
                  aria-label="Scroll authors right"
                  v-on:click="scrollRow('authorScroller', 1)">
            ›
          </button>
          <button type="button"
                  class="scroll-row-arrow scroll-row-toggle"
                  :class="{'is-hidden': !authorHasOverflow && !authorExpanded}"
                  :aria-label="authorExpanded ? 'Collapse authors' : 'Show all authors'"
                  :title="authorExpanded ? 'Collapse' : 'Show all'"
                  v-on:click="toggleAuthorExpand">
            {{ authorExpanded ? '«' : '»' }}
          </button>
        </div>

        <!-- Top Tags Filter Row -->
        <div class="scroll-row-wrapper" :class="{'scroll-row-wrapper--expanded': tagExpanded}">
          <div class="filter-pill scroll-row-all"
               :class="{'active': (tagFilter === 'all')}"
               v-on:click="changeTagFilter('all')">
            All Top Tags
          </div>
          <button v-if="!tagExpanded" type="button"
                  class="scroll-row-arrow scroll-row-arrow--left"
                  :class="{'is-disabled': !tagCanScrollLeft, 'is-hidden': !tagHasOverflow}"
                  aria-label="Scroll tags left"
                  v-on:click="scrollRow('tagScroller', -1)">
            ‹
          </button>
          <div class="filter-row"
               :class="tagExpanded ? 'filter-row--wrap' : 'filter-row--scroll'"
               ref="tagScroller"
               v-on:wheel.prevent="onRowWheel('tagScroller', $event)"
               v-on:scroll="updateTagScrollState">
            <div v-for="(tag, index) in topTags" :key="'tag-'+index"
                 class="filter-pill" :class="{'active': (tagFilter === tag.tag)}"
                 v-on:click="changeTagFilter(tag.tag)">
              {{ tag.tag }}
            </div>
          </div>
          <button v-if="!tagExpanded" type="button"
                  class="scroll-row-arrow scroll-row-arrow--right"
                  :class="{'is-disabled': !tagCanScrollRight, 'is-hidden': !tagHasOverflow}"
                  aria-label="Scroll tags right"
                  v-on:click="scrollRow('tagScroller', 1)">
            ›
          </button>
          <button type="button"
                  class="scroll-row-arrow scroll-row-toggle"
                  :class="{'is-hidden': !tagHasOverflow && !tagExpanded}"
                  :aria-label="tagExpanded ? 'Collapse tags' : 'Show all tags'"
                  :title="tagExpanded ? 'Collapse' : 'Show all'"
                  v-on:click="toggleTagExpand">
            {{ tagExpanded ? '«' : '»' }}
          </button>
        </div>
      </div>

      <div class="filter-header-label mb-3 mt-4">
        ⇄ Options
      </div>

      <div class="control-main-container p-4 mb-4">
        <div class="control-row">
          <span class="control-row-label">Mode</span>
          <div class="control-pill"
               :class="{'active': viewMode === 'view'}"
               v-on:click="setViewMode('view')">
            View
          </div>
          <div class="control-pill"
               :class="{'active': viewMode === 'drag'}"
               v-on:click="setViewMode('drag')">
            Drag
          </div>
          <a href="/#/" class="control-pill control-pill--link">
            Show grid ↗
          </a>
        </div>
      </div>

      <d3-network
          ref='net'
          :net-nodes="nodes"
          :net-links="links"
          :options="options"
          @node-click="nodeClickHandler"
          @node-drag-start="handleNodeDragStart"
          @node-drag="handleNodeDrag"
          @node-drag-end="handleNodeDragEnd"
      />
    </div>
  </div>
</template>

<script>
import D3Network from 'vue-d3-network';
import CardService from "../../services/CardService";
import * as d3 from 'd3';
import EventBus from "../../event-bus";

export default {
  name: 'CardMapViewerComponent',
  components: {
    D3Network
  },
  computed: {
    user() {
      return this.$store.state.user
    },
    categoryHasOverflow() {
      return this.categoryCanScrollLeft || this.categoryCanScrollRight
    },
    authorHasOverflow() {
      return this.authorCanScrollLeft || this.authorCanScrollRight
    },
    tagHasOverflow() {
      return this.tagCanScrollLeft || this.tagCanScrollRight
    },
    options() {
      return {
        force: 1500,
        size: {h: 1000},
        nodeSize: this.nodeSize,
        nodeLabels: true,
        linkLabels: true,
        canvas: this.canvas,
        // zoom: this.zoom, // Remove zoom from options
      };
    }
  },
  methods: {
    nodeClickHandler(event, nodeObject) {
      // Logic for double click to open, single click to select
      if (this.selectedCard === nodeObject.id) {
        // "Double click" detected (second click on same node)

        const index = this.nodes.findIndex(n => n.id === nodeObject.id);

        if (index !== -1) {
          const container = this.$refs.net.$el;
          // Consistent selector
          let allNodes = container.querySelectorAll('circle');
          const targetEl = allNodes[index];

          if (targetEl) {
            const d3Node = d3.select(targetEl);

            // Use baseNodeSize for scaling calculations
            const currentBase = this.baseNodeSize || this.nodeSize;

            // Animation: Pulse (Green)
            d3Node.transition()
                .duration(200)
                .attr('r', currentBase * 1.25)
                .style('stroke', '#28a745')
                .style('stroke-width', '5px')
                .transition()
                .duration(200)
                .attr('r', currentBase * 1.15); // Return to "Selected" size
          }
        }

        // Open card in a new window after short delay
        setTimeout(() => {
          const route = this.$router.resolve('/card/' + nodeObject.id);
          window.open(route.href, '_blank');
        }, 300);

      } else {
        // Single click / New selection

        const container = this.$refs.net.$el;

        // 1. Reset PREVIOUSLY Selected Nodes Only
        // We use a specific class to track them, preventing global resets.
        const prevSelected = container.querySelectorAll('.selected-node');

        // Default restore size
        // If we haven't detected base size yet, we might fallback to 'r' attribute or nodeSize.
        // It is safer to read 'r' from the element itself if possible, but for reset we need a target.
        const restoreSize = this.baseNodeSize || this.nodeSize;

        d3.selectAll(prevSelected)
            .classed('selected-node', false) // Remove class
            .transition().duration(200)
            .attr('r', restoreSize); // Reset to base size

        // 2. Set New Selection State
        this.selectedCard = nodeObject.id;

        // 3. Highlight New Selection
        const index = this.nodes.findIndex(n => n.id === nodeObject.id);
        const allNodes = container.querySelectorAll('circle');

        if (index !== -1 && allNodes[index]) {
          const targetD3 = d3.select(allNodes[index]);

          // CAPTURE BASE SIZE (First time interaction)
          // We read the CURRENT radius before expanding.
          // Ideally we do this only if not already set, or if we trust the initial state.
          if (!this.baseNodeSize) {
            const currentR = parseFloat(targetD3.attr('r'));
            // Only set if reliable number
            if (!isNaN(currentR) && currentR > 0) {
              this.baseNodeSize = currentR;
            } else {
              this.baseNodeSize = this.nodeSize;
            }
          }

          const targetSize = this.baseNodeSize || this.nodeSize;

          targetD3
              .classed('selected-node', true) // Mark as selected
              .transition().duration(200)
              .attr('r', targetSize * 1.15); // Expand relative to base
        }
      }
    },
    handleZoom(event) {
      if (this.viewMode === 'view') {
        this.isZooming = true;
        this.isDragging = false;
        const container = d3.select(this.$refs.net.$el).select('svg > g');
        if (container.node()) {
          container.attr('transform', event.transform);
        }
        const nodes = d3.select(this.$refs.net.$el).selectAll('.node');
        nodes.attr('transform', event.transform);
        const labels = d3.select(this.$refs.net.$el).selectAll('.node-label');
        labels.attr('transform', event.transform);
      }
    },
    handleZoomEnd() {
      if (this.viewMode === 'view') {
        this.isZooming = false;
      }
    },
    handleNodeDragStart() {
      if (this.viewMode === 'drag') {
        this.isDragging = true;
        this.cancelZoom();
      }
    },
    handleNodeDrag() {
      if (this.viewMode === 'drag') {
        this.isDragging = true;
        this.cancelZoom();
      }
    },
    handleNodeDragEnd() {
      if (this.viewMode === 'drag') {
        this.isDragging = false;
      }
    },
    cancelZoom() {
      if (this.zoomBehavior && this.$refs.net && this.$refs.net.$el) {
        const svg = d3.select(this.$refs.net.$el).select('svg');
        svg.on(".zoom", null);
      }
    },
    enableZoom() {
      if (this.zoomBehavior && this.$refs.net && this.$refs.net.$el) {
        const svg = d3.select(this.$refs.net.$el).select('svg');
        svg.call(this.zoomBehavior);
      }
    },
    setViewMode(mode) {
      this.viewMode = mode;

      this.$nextTick(() => {
        if (mode === 'view') {
          // ENABLE View Mode: Enable Zoom
          this.enableZoom();
        } else {
          // ENABLE Drag Mode: Disable Zoom
          this.cancelZoom();
          this.isZooming = false;
        }
      });
    },
    captureStart(event) {
      // Capture Phase: Block Drag Start
      if (this.viewMode === 'view') {
        const target = event.target;
        // Check if node (circle or class .node)
        const isNode = target.tagName === 'circle' || (target.classList && target.classList.contains('node'));

        if (isNode) {
          // STOP D3 Drag
          event.stopPropagation();
        }
      }
    },
    captureClick(event) {
      // Capture Phase: Detect Click on Node
      if (this.viewMode === 'view') {
        const target = event.target;
        const isNode = target.tagName === 'circle' || (target.classList && target.classList.contains('node'));

        if (isNode) {
          // Stop propagation to prevent native/D3 handlers (and double firing)
          event.stopPropagation();
          event.preventDefault();

          // Find data by index mapping
          const container = this.$refs.net.$el;
          const allNodes = Array.from(container.querySelectorAll('circle'));
          const index = allNodes.indexOf(target);

          if (index !== -1 && this.nodes[index]) {
            const nodeData = this.nodes[index];
            this.nodeClickHandler(event, nodeData);
          }
        }
      }
    },
    loadConnections: function () {
      let categoryFilter = this.filter === 'all' ? null : this.filter
      let tagFilter = this.tagFilter === 'all' ? null : this.tagFilter
      // "My Cards" maps to the user's own company id, which is the value shape the backend's author filter expects.
      let authorFilter
      if (this.authorFilter === 'mine' && this.user.loggedIn) {
        authorFilter = this.user.company && this.user.company.idCompany
      } else if (this.authorFilter === 'all') {
        authorFilter = null
      } else {
        authorFilter = this.authorFilter
      }

      CardService.getCardConnections(categoryFilter, tagFilter, authorFilter).then(response => {
        let rawNodes = response.data['nodes']
        let rawConnections = response.data['connections']

        this.nodes = rawNodes.map(node => ({
          id: node.idCard,
          name: node.title,
          _color: node.cardColor,
          _cssClass: (node.cardColor === '#ffffff' || node.cardColor === 'white' || node.cardColor?.toLowerCase() === '#fff') ? 'white-node' : ''
        }));

        this.links = rawConnections.map(connection => ({
          sid: connection.idCard,
          tid: connection.connectedCard,
          _color: "black"
        }));
      });
    },
    changeFilter(filter) {
      this.filter = filter
      this.loadConnections()
    },
    changeAuthorFilter(filter) {
      this.authorFilter = filter
      this.loadConnections()
    },
    changeTagFilter(filter) {
      this.tagFilter = filter
      this.loadConnections()
    },
    onRowWheel(refName, event) {
      const el = this.$refs[refName]
      if (!el) return
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
      el.scrollLeft += delta
    },
    scrollRow(refName, direction) {
      const el = this.$refs[refName]
      if (!el) return
      el.scrollBy({left: direction * Math.max(el.clientWidth * 0.8, 200), behavior: 'smooth'})
    },
    computeScrollFlags(refName) {
      const el = this.$refs[refName]
      if (!el) return {left: false, right: false}
      return {
        left: el.scrollLeft > 0,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1
      }
    },
    updateAuthorScrollState() {
      const f = this.computeScrollFlags('authorScroller')
      this.authorCanScrollLeft = f.left
      this.authorCanScrollRight = f.right
    },
    updateCategoryScrollState() {
      const f = this.computeScrollFlags('categoryScroller')
      this.categoryCanScrollLeft = f.left
      this.categoryCanScrollRight = f.right
    },
    updateTagScrollState() {
      const f = this.computeScrollFlags('tagScroller')
      this.tagCanScrollLeft = f.left
      this.tagCanScrollRight = f.right
    },
    updateAllScrollStates() {
      this.updateAuthorScrollState()
      this.updateCategoryScrollState()
      this.updateTagScrollState()
    },
    toggleCategoryExpand() {
      this.categoryExpanded = !this.categoryExpanded
      if (!this.categoryExpanded) {
        this.$nextTick(this.updateCategoryScrollState)
      }
    },
    toggleAuthorExpand() {
      this.authorExpanded = !this.authorExpanded
      if (!this.authorExpanded) {
        this.$nextTick(this.updateAuthorScrollState)
      }
    },
    toggleTagExpand() {
      this.tagExpanded = !this.tagExpanded
      if (!this.tagExpanded) {
        this.$nextTick(this.updateTagScrollState)
      }
    },
  },
  mounted() {
    EventBus.$on('search-card', resp => {
      this.collapsed = 'collapse show'
      window.find(resp)
      document.getElementById('search').focus().select()

    })

    this.loadConnections();

    CardService.getAllCardCategories().then((response) => {
      this.cardCategories = response.data
      this.$nextTick(this.updateCategoryScrollState)
    })

    CardService.getMostUsedTags().then((response) => {
      this.topTags = response.data
      this.$nextTick(this.updateTagScrollState)
    })

    CardService.getAllCardAuthors().then((response) => {
      this.authors = response.data
      this.$nextTick(this.updateAuthorScrollState)
    })

    window.addEventListener('resize', this.updateAllScrollStates)

    this.$nextTick(() => {
      // eslint-disable-next-line
      const svg = d3.select(this.$refs.net.$el).select('svg');
      this.zoomBehavior = d3.zoom()
          .scaleExtent([0.1, 10])
          .on('zoom', this.handleZoom)
          .on('end', this.handleZoomEnd);

      // Initial mode setup
      this.setViewMode(this.viewMode); // Call setViewMode to set initial state

      // BLOCKING LISTENERS (Capture Phase)
      // We block pointerdown/mousedown/touchstart to stop D3 drag
      const el = this.$el;
      const opts = {capture: true};

      el.addEventListener('mousedown', this.captureStart, opts);
      el.addEventListener('touchstart', this.captureStart, opts);
      el.addEventListener('pointerdown', this.captureStart, opts);

      // CLICK LISTENER (Capture Phase)
      // We block native click and substitute our own logic
      el.addEventListener('click', this.captureClick, opts);
    });
  },
  beforeDestroy() {
    const el = this.$el;
    const opts = {capture: true};

    el.removeEventListener('mousedown', this.captureStart, opts);
    el.removeEventListener('touchstart', this.captureStart, opts);
    el.removeEventListener('pointerdown', this.captureStart, opts);

    el.removeEventListener('click', this.captureClick, opts);

    window.removeEventListener('resize', this.updateAllScrollStates)
  },
  data() {
    return {
      nodes: [],
      links: [],
      nodeSize: 30,
      canvas: false,
      strLinks: true,
      selectedCard: null,
      zoomBehavior: null,
      isZooming: false,
      isDragging: false,
      viewMode: 'view', // 'view' or 'drag'
      baseNodeSize: null, // Stores the initial radius of nodes dynamically
      cardCategories: [],
      authors: [],
      topTags: [],
      filter: 'all',
      authorFilter: 'all',
      tagFilter: 'all',
      categoryCanScrollLeft: false,
      categoryCanScrollRight: false,
      authorCanScrollLeft: false,
      authorCanScrollRight: false,
      tagCanScrollLeft: false,
      tagCanScrollRight: false,
      categoryExpanded: false,
      authorExpanded: false,
      tagExpanded: false,
    };
  }
};
</script>

<style scoped>
/* Common Filter Styles */
.filter-main-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  margin-top: 10px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.filter-row:last-child {
  margin-bottom: 0;
}

.scroll-row-wrapper {

  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 15px;
}

.scroll-row-wrapper:last-child {
  margin-bottom: 0;
}

.scroll-row-all {
  flex: 0 0 auto;
}

.filter-row--scroll {
  flex: 1 1 auto;
  flex-wrap: nowrap;
  margin-bottom: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 2px 4px;
  min-width: 0;
}

.filter-row--scroll::-webkit-scrollbar {
  display: none;
}

.filter-row--scroll .filter-pill {
  flex: 0 0 auto;
}

.scroll-row-arrow {
  flex: 0 0 auto;
  width: 20px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #000;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.scroll-row-arrow.is-disabled {
  opacity: 0.2;
  cursor: default;
  pointer-events: none;
}

.scroll-row-arrow.is-hidden {
  visibility: hidden;
  pointer-events: none;
}

.scroll-row-toggle {
  font-size: 18px;
  font-weight: 600;
}

.scroll-row-wrapper--expanded {
  align-items: flex-start;
}

.scroll-row-wrapper--expanded .scroll-row-all {
  margin-top: 2px;
}

.scroll-row-wrapper--expanded .scroll-row-toggle {
  margin-top: 2px;
}

.filter-row--wrap {
  flex: 1 1 auto;
  flex-wrap: wrap;
  margin-bottom: 0;
  overflow: visible;
  padding: 2px 4px;
  min-width: 0;
}

.filter-row--wrap .filter-pill {
  flex: 0 0 auto;
}

.filter-pill {
  border: 1px solid #000;
  border-radius: 8px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 14px;
  background: #fff;
  color: #000;
  transition: all 0.2s ease;
  font-family: Arial, sans-serif;
}

.filter-pill.active {
  background: #000 !important;
  color: #fff !important;
}

.filter-header-label {
  font-weight: normal;
  font-size: 18px;
  color: #000;
  text-decoration: underline;
  cursor: pointer;
}

/* Map control (options) styles — visually related to filter pills but
   set apart via a fully-rounded shape, a labeled row, and a dashed
   accent on the navigation link, so they read as controls not filters. */
.control-main-container {
  background: #fafafa;
  border: 1px dashed #bbb;
  border-radius: 8px;
  margin-top: 10px;
}

.control-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.control-row-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #555;
  margin-right: 4px;
}

.control-pill {
  border: 1px solid #000;
  border-radius: 999px;
  padding: 6px 16px;
  cursor: pointer;
  font-size: 14px;
  background: #fff;
  color: #000;
  transition: all 0.2s ease;
  font-family: Arial, sans-serif;
  text-decoration: none;
  display: inline-block;
  line-height: 1.4;
}

.control-pill:hover {
  background: #f0f0f0;
}

.control-pill.active {
  background: #000 !important;
  color: #fff !important;
}

.control-pill--link {
  margin-left: auto;
  border-style: dashed;
}

.control-pill--link:hover {
  background: #f0f0f0;
  color: #000;
}

::v-deep #l-nodes > circle, ::v-deep .node {
  stroke: none;
}

::v-deep #l-nodes > circle.white-node, ::v-deep .node.white-node {
  stroke: black !important;
  stroke-width: 1px !important;
}

::v-deep #l-nodes > circle.selected-node, ::v-deep .node.selected-node {
  stroke: #007bff !important;
  stroke-width: 2px !important;
}
</style>
